import React, { type ComponentType } from 'react';

import { type IntlShape } from 'react-intl-next';
import Loadable from 'react-loadable';

import { DefaultExtensionProvider, type ExtensionParams } from '@atlaskit/editor-common/extensions';
import { token } from '@atlaskit/tokens';
import { DEFAULT_GRAY_COLOR } from '@atlassian/color-picker';
import {
	Alignment,
	CornerStyle,
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
	KNOWLEDGE_CARDS_EXTENSION_TYPE,
	Orientation,
	type ProductDependenciesProviderProps,
	Height as Size,
} from '@atlassian/company-hub-common';

import {
	KNOWLEDGE_CARDS_EXTENSION_KEY,
	KNOWLEDGE_CARDS_EXTENSION_VERSION_1,
} from '../../constants';
import { type KnowledgeCardsParameters } from '../../types';

import { i18n } from './messages';

const KnowledgeCardsExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_KnowledgeCardsExtension" */ '../knowledge-cards-extension'
			)
		).KnowledgeCardsExtension,
	loading: () => <></>,
});

export const getInitialKnowledgeCardsParametersV1 = (
	intl?: IntlShape,
): KnowledgeCardsParameters => ({
	version: KNOWLEDGE_CARDS_EXTENSION_VERSION_1,
	extensionTitle: intl?.formatMessage(i18n.title) || '',
	orientation: Orientation.STACKED,
	isPaddingEnabled: true,
	cornerStyle: CornerStyle.ROUNDED,
	backgroundColor: token('elevation.surface'),
	textColor: token('color.text'),
	borderColor: DEFAULT_GRAY_COLOR,
	alignment: Alignment.CENTER,
	size: Size.MEDIUM,
	isAutoscrollEnabled: false,
	// Should be empty since it will be populated by API call
	cards: [],
});

export type KnowledgeCardsExtensionProviderWrapperProps = {
	contentId: string;
	isInEditor: boolean;
	propsProvider: ComponentType<ProductDependenciesProviderProps>;
};

export const knowledgeCardsExtensionProvider = async ({
	contentId,
	propsProvider,
	isInEditor,
}: KnowledgeCardsExtensionProviderWrapperProps) => {
	return async ({ editorActions, openConfigPanel, intl }: CustomSitesExtensionProviderProps) => {
		const title = intl.formatMessage(i18n.title);
		const description = intl.formatMessage(i18n.description);
		const type = KNOWLEDGE_CARDS_EXTENSION_TYPE;
		const render = async () => {
			return ({ node }: { node: ExtensionParams<KnowledgeCardsParameters> }) => (
				<KnowledgeCardsExtension
					contentId={contentId}
					isInEditor={isInEditor}
					propsProvider={propsProvider}
					extensionNode={node}
				/>
			);
		};
		const icons = {
			// ADD correct icon. this is a placeholder for now
			// eslint-disable-next-line import/dynamic-import-chunkname
			'48': () =>
				import(
					/* webpackChunkName: "@atlaskit-internal_KnowledgeCardsExtensionIcon" */
					'./knowledge-cards-extension-icon/index'
				).then((mod) => mod.KnowledgeCardsExtensionIcon),
		};

		const parameters = getInitialKnowledgeCardsParametersV1(intl);

		const update = async () => {
			const selectedNode = editorActions?.getSelectedNode()?.toJSON();
			const selectedLocalId = selectedNode?.attrs?.localId;

			if (!!selectedLocalId && openConfigPanel) {
				openConfigPanel(selectedLocalId);
			}
		};

		return new DefaultExtensionProvider([
			getExtensionManifest<KnowledgeCardsParameters>({
				key: KNOWLEDGE_CARDS_EXTENSION_KEY,
				icons,
				parameters,
				render,
				update,
				title,
				type,
				description,
				source: 'premiumKnowledgeCardsInsert',
			}),
		]);
	};
};
